/**
 * /admin/capital-gains — the capital-gains tool (admin-only, `capital_gains.run`).
 *
 * Upload one client's custodian portfolio exports (one .xlsx per portfolio) →
 * Preview (every file is read and checked; the tax is shown) → Download workbook
 * (the full review workbook). Spec: `docs/capital-gains-procedure.md`.
 *
 * Stateless: the files stay in the browser and are re-sent on each call; the backend
 * stores nothing about the transactions. Each Preview / Download writes one audit row
 * (metadata only); the run ID is printed on the workbook's Summary sheet.
 *
 * The preview belongs to the inputs it was made from: change any input (a file, a label,
 * a date, a rate, a transfer-in correction) and the preview is marked out of date and
 * Download is disabled until you preview again (§3j: the screen never shows figures
 * for inputs other than the ones on it).
 *
 * "Partial history" (decision 2026-10-16): each export starts on day 1, its oldest operation.
 * The preview states what each security held before day 1 (worked out back from today), the
 * date from which every lot is certified, and the lots on that date and on an optional
 * chosen date. Anything built on the units held before day 1 shows "unknown", and a tax it
 * would feed shows "not computable" — never a figure.
 */
import { useMemo, useState } from 'react';
import {
  ActionIcon, Alert, Badge, Card, Container, Group, List, NumberInput, Radio, Stack, Table, Text,
  TextInput, ThemeIcon, Title, Tooltip,
} from '@mantine/core';
import { Dropzone } from '@mantine/dropzone';
import { notifications } from '@mantine/notifications';
import {
  IconAlertTriangle, IconCheck, IconDownload, IconFileSpreadsheet, IconSearch, IconTrash,
  IconUpload, IconX,
} from '@tabler/icons-react';

import {
  capitalGainsApi, type CgInputs, type CgOpening, type CgOpeningUnits, type CgOverride, type CgPreview,
  type CgQuestion, type CgSnapshot, type CgTransferInLot, type CgWindow,
} from '../api/capitalGains';
import { downloadBlob } from '../api/iaaExports';
import { BackToCompliance } from '../components/BackToCompliance';
import { DisabledTooltipButton } from '../components/DisabledTooltipButton';
import { RequireAccess } from '../components/RequireAccess';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { useCurrentUser } from '../hooks/useAuth';
import { localTodayISO } from '../utils/localDate';

const XLSX_ACCEPT = { 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'] };
const MAX_FILES = 20;

const errMsg = (e: unknown) => (e instanceof Error ? e.message : 'Something went wrong.');
const stem = (name: string) => name.replace(/\.[^.]+$/, '');
const eur = (s: string | null | undefined) =>
  s == null ? '—' : Number(s).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const qty = (s: string) => Number(s).toLocaleString(undefined, { maximumFractionDigits: 6 });
const pct = (ratio: string) => `${(Number(ratio) * 100).toLocaleString(undefined, { maximumFractionDigits: 2 })}%`;
const kb = (bytes: number) => `${Math.max(1, Math.round(bytes / 1024)).toLocaleString()} KB`;
/** A tax figure; null = the group has uncertified sales (partial history): never shown as 0 or "—". */
const tax = (s: string | null) => (s == null ? 'not computable' : eur(s));
const dayBefore = (iso: string) => {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() - 1);
  return d.toISOString().slice(0, 10);
};

interface Picked { id: string; file: File; label: string }
interface Correction { acquiredOn: string; costEur: number | '' }

export function CapitalGainsPage() {
  useDocumentTitle('Capital gains');
  // P45 (Pedro 2026-10-15): RM and Compliance hold capital_gains.run too (migration 0106);
  // they reach the page from the Welcome page, so the way back goes there.
  const { user } = useCurrentUser();
  return (
    <RequireAccess permission="capital_gains.run">
      <Container size="xl">
        <Stack gap="lg">
          {user?.role === 'admin'
            ? <BackToCompliance to="/admin" label="Back to Admin" />
            : <BackToCompliance to="/" label="Back to Welcome" />}
          <CapitalGainsTool />
        </Stack>
      </Container>
    </RequireAccess>
  );
}

function CapitalGainsTool() {
  const [clientLabel, setClientLabel] = useState('');
  const [asOf, setAsOf] = useState(localTodayISO());
  const [priceDate, setPriceDate] = useState('');
  const [shortPct, setShortPct] = useState<number | ''>(48);
  const [longPct, setLongPct] = useState<number | ''>(28);
  const [files, setFiles] = useState<Picked[]>([]);
  const [corrections, setCorrections] = useState<Record<string, Correction>>({});
  const [preview, setPreview] = useState<CgPreview | null>(null);
  const [previewedSig, setPreviewedSig] = useState<string | null>(null);
  const [busy, setBusy] = useState<'preview' | 'download' | null>(null);

  // Corrections are keyed by the FILE (its stable id), not its label: a rename keeps them,
  // removing the file drops them. Sent with the file's CURRENT label.
  const [previewIds, setPreviewIds] = useState<Record<string, string>>({});   // label at preview → file id
  const overrides: CgOverride[] = useMemo(() => Object.entries(corrections)
    .filter(([, c]) => c.acquiredOn)
    .flatMap(([key, c]) => {
      const [fileId, instrument, op_code] = key.split('\u0001');
      const file = files.find((f) => f.id === fileId);
      return file ? [{
        portfolio: file.label.trim(), instrument, op_code, acquired_on: c.acquiredOn,
        cost_eur: c.costEur === '' ? null : String(c.costEur),
      }] : [];
    }), [corrections, files]);
  const keyOf = (lot: CgTransferInLot) =>
    `${previewIds[lot.portfolio] ?? lot.portfolio}\u0001${lot.instrument}\u0001${lot.op_code}`;

  // Answers to the preview's questions — keyed by file like the corrections, sent with the
  // file's current label in the key the backend expects (kind|label|instrument).
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const decisions = useMemo(() => Object.entries(answers)
    .filter(([, a]) => a)
    .flatMap(([key, answer]) => {
      const [fileId, kind, instrument] = key.split('\u0001');
      const file = files.find((f) => f.id === fileId);
      return file ? [{ key: `${kind}|${file.label.trim()}|${instrument}`, answer }] : [];
    }), [answers, files]);
  const questionKeyOf = (q: CgQuestion) =>
    `${previewIds[q.portfolio] ?? q.portfolio}\u0001${q.kind}\u0001${q.instrument}`;

  // Partial history: the history mode, an optional "lots on" date, and the user's true date
  // and EUR cost for units held before day 1 — keyed by file like the other corrections.
  const [history, setHistory] = useState<'full' | 'partial'>('full');
  const [lotsDate, setLotsDate] = useState('');
  const [openingCorr, setOpeningCorr] = useState<Record<string, Correction>>({});
  const opening: CgOpening[] = useMemo(() => Object.entries(openingCorr)
    .filter(([, c]) => c.acquiredOn && c.costEur !== '')
    .flatMap(([key, c]) => {
      const [fileId, instrument] = key.split('\u0001');
      const file = files.find((f) => f.id === fileId);
      return file ? [{ portfolio: file.label.trim(), instrument, acquired_on: c.acquiredOn, cost_eur: String(c.costEur) }] : [];
    }), [openingCorr, files]);
  const openingKeyOf = (o: CgOpeningUnits) => `${previewIds[o.portfolio] ?? o.portfolio}\u0001${o.instrument}`;

  const inputs: CgInputs = {
    files: files.map((f) => ({ file: f.file, label: f.label.trim() })),
    clientLabel: clientLabel.trim(), asOf, priceDate,
    shortRatePct: shortPct === '' ? NaN : shortPct, longRatePct: longPct === '' ? NaN : longPct,
    overrides, decisions, history, opening: history === 'partial' ? opening : [], lotsDate,
  };
  // What the preview was made from — any change makes it out of date.
  const signature = JSON.stringify({
    f: files.map((f) => [f.id, f.label.trim()]), c: inputs.clientLabel, asOf, priceDate, shortPct, longPct, overrides,
    decisions, history, opening: inputs.opening, lotsDate,
  });
  const stale = preview != null && previewedSig !== signature;

  const labels = files.map((f) => f.label.trim());
  const dupLabel = labels.find((l, i) => l && labels.indexOf(l) !== i);
  const ratesOk = [shortPct, longPct].every((p) => p !== '' && p >= 0 && p <= 100);
  const blockReason =
    files.length === 0 ? 'Add at least one portfolio export first'
    : !inputs.clientLabel ? 'Enter the client label first'
    : !asOf ? 'Enter the calculation date first'
    : labels.some((l) => !l) ? 'Every file needs a portfolio label'
    : dupLabel ? `Two files share the portfolio label "${dupLabel}"`
    : !ratesOk ? 'Both rates must be between 0% and 100%'
    : lotsDate && asOf && lotsDate > asOf ? 'The "lots on" date cannot be after the calculation date'
    : null;
  const downloadReason = blockReason
    ?? (!preview ? 'Preview first: the workbook is produced from a checked run'
      : stale ? 'The inputs changed since the preview: preview again'
      : !preview.publishable ? 'The preview found problems that stop the run (see above)'
      : null);

  const addFiles = (dropped: File[]) => {
    setFiles((prev) => {
      const seen = new Set(prev.map((p) => `${p.file.name}\u0001${p.file.size}`));
      const fresh = dropped
        .filter((f) => !seen.has(`${f.name}\u0001${f.size}`))
        .map((f, i) => ({ id: `${Date.now()}-${i}-${f.name}`, file: f, label: stem(f.name) }));
      const next = [...prev, ...fresh];
      if (next.length > MAX_FILES) {
        notifications.show({ color: 'red', title: 'Too many files', message: `At most ${MAX_FILES} exports per run.` });
        return prev;
      }
      return next;
    });
  };

  const runPreview = async () => {
    setBusy('preview');
    try {
      const pv = await capitalGainsApi.preview(inputs);
      setPreview(pv);
      setPreviewedSig(signature);
      setPreviewIds(Object.fromEntries(files.map((f) => [f.label.trim(), f.id])));
    } catch (e) {
      notifications.show({ color: 'red', title: 'Preview failed', message: errMsg(e) });
    } finally {
      setBusy(null);
    }
  };

  const runDownload = async () => {
    setBusy('download');
    try {
      const { blob, fileName, runId } = await capitalGainsApi.workbook(inputs);
      downloadBlob(blob, fileName);
      notifications.show({
        color: 'green', title: 'Workbook downloaded',
        message: `${fileName}${runId ? ` · run ID ${runId.slice(0, 8)}… (recorded in the Audit log)` : ''}`,
      });
    } catch (e) {
      notifications.show({ color: 'red', title: 'Workbook not produced', message: errMsg(e) });
    } finally {
      setBusy(null);
    }
  };

  return (
    <Stack gap="lg">
      <div>
        <Title order={2}>Capital gains</Title>
        <Text size="sm" c="dimmed">
          Rebuild a client&apos;s portfolios from the custodian&apos;s exports — FIFO lots, realised gains and the
          capital-gains tax per calendar year (≤365 days and &gt;365 days, netted per group across all the client&apos;s
          portfolios). <b>Preview</b> reads and checks every file; <b>Download workbook</b> produces the full review
          workbook. Nothing about the transactions is stored; each run is recorded in the Audit log (no amounts).
        </Text>
      </div>

      <Card withBorder>
        <Stack gap="sm">
          <Text fw={600}>1 · Client, dates and rates</Text>
          <Group align="flex-start" gap="md">
            <TextInput label="Client label" placeholder="e.g. the client code" required w={260}
              value={clientLabel} onChange={(e) => setClientLabel(e.currentTarget.value)} />
            <TextInput label="Calculation date" type="date" required w={180}
              value={asOf} onChange={(e) => setAsOf(e.currentTarget.value)} />
            <TextInput label="Price date" type="date" w={180}
              value={priceDate} onChange={(e) => setPriceDate(e.currentTarget.value)} />
            <NumberInput label="Rate, ≤365 days" min={0} max={100} step={0.5} decimalScale={2} suffix=" %" w={150}
              value={shortPct} onChange={(v) => setShortPct(typeof v === 'number' ? v : '')} />
            <NumberInput label="Rate, >365 days" min={0} max={100} step={0.5} decimalScale={2} suffix=" %" w={150}
              value={longPct} onChange={(v) => setLongPct(typeof v === 'number' ? v : '')} />
          </Group>
          <Text size="xs" c="dimmed">
            The calculation date is the reference for days held, days to qualify and “if sold today”. The price date is
            the date of the custodian&apos;s valuation prices; leave it blank to use the calculation date.
          </Text>
          <Group align="flex-start" gap="xl">
            <Radio.Group label="History in the exports" value={history}
              onChange={(v) => setHistory(v === 'partial' ? 'partial' : 'full')}>
              <Stack gap={4} mt={4}>
                <Radio value="full" label="Full history — every operation since the portfolio started" />
                <Radio value="partial" label="Partial history — the export starts later (e.g. the last 5 years)" />
              </Stack>
            </Radio.Group>
            <TextInput label="Also show the lots on" type="date" w={180} max={asOf || undefined}
              description="Optional: positions and FIFO lots at the end of this date"
              value={lotsDate} onChange={(e) => setLotsDate(e.currentTarget.value)} />
          </Group>
          {history === 'partial' && (
            <Text size="xs" c="dimmed">
              Partial history: each export&apos;s history starts on its oldest operation date (day 1). What was held
              before day 1 is worked out back from the custodian&apos;s quantities; its purchase dates and costs are not
              in the export. A sale that used those units has no cost or gain, and a year&apos;s tax group that contains
              one is “not computable”. The preview states the date from which every lot is known.
            </Text>
          )}
        </Stack>
      </Card>

      <Card withBorder>
        <Stack gap="sm">
          <Text fw={600}>2 · Portfolio exports</Text>
          <Dropzone onDrop={addFiles} accept={XLSX_ACCEPT} multiple loading={busy != null}
            onReject={() => notifications.show({ color: 'red', title: 'Not accepted', message: 'Please choose .xlsx workbooks.' })}>
            <Group justify="center" gap="xl" mih={110} style={{ pointerEvents: 'none' }}>
              <Dropzone.Accept><IconUpload size={40} stroke={1.5} /></Dropzone.Accept>
              <Dropzone.Reject><IconX size={40} stroke={1.5} /></Dropzone.Reject>
              <Dropzone.Idle><IconFileSpreadsheet size={40} stroke={1.5} /></Dropzone.Idle>
              <div>
                <Text size="lg" inline>Drag the client&apos;s exports here, or click to choose</Text>
                <Text size="sm" c="dimmed" inline mt={7} display="block">
                  One .xlsx per portfolio: the operations sheet plus the custodian valuation sheet. Add more files at
                  any time; each drop adds to the list.
                </Text>
              </div>
            </Group>
          </Dropzone>
          {files.length > 0 && (
            <Table striped withTableBorder verticalSpacing={4}>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>File</Table.Th><Table.Th>Portfolio label</Table.Th><Table.Th>Size</Table.Th><Table.Th />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {files.map((f) => (
                  <Table.Tr key={f.id}>
                    <Table.Td><Text size="sm">{f.file.name}</Text></Table.Td>
                    <Table.Td>
                      <TextInput size="xs" w={240} value={f.label}
                        error={!f.label.trim() ? 'Required' : f.label.trim() === dupLabel ? 'Must be unique' : undefined}
                        onChange={(e) => {
                          const v = e.currentTarget.value;
                          setFiles((prev) => prev.map((p) => (p.id === f.id ? { ...p, label: v } : p)));
                        }} />
                    </Table.Td>
                    <Table.Td><Text size="sm" c="dimmed">{kb(f.file.size)}</Text></Table.Td>
                    <Table.Td>
                      <Tooltip label="Remove this file" withArrow>
                        <ActionIcon variant="subtle" color="red" aria-label="Remove this file"
                          onClick={() => {
                            setFiles((prev) => prev.filter((p) => p.id !== f.id));
                            setCorrections((prev) => Object.fromEntries(
                              Object.entries(prev).filter(([k]) => !k.startsWith(`${f.id}\u0001`))));
                            setAnswers((prev) => Object.fromEntries(
                              Object.entries(prev).filter(([k]) => !k.startsWith(`${f.id}\u0001`))));
                            setOpeningCorr((prev) => Object.fromEntries(
                              Object.entries(prev).filter(([k]) => !k.startsWith(`${f.id}\u0001`))));
                          }}>
                          <IconTrash size={16} />
                        </ActionIcon>
                      </Tooltip>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          )}
        </Stack>
      </Card>

      <Group>
        <DisabledTooltipButton disabledReason={blockReason} onClick={runPreview} loading={busy === 'preview'}
          leftSection={<IconSearch size={16} />}>
          Preview
        </DisabledTooltipButton>
        <DisabledTooltipButton disabledReason={downloadReason} onClick={runDownload} loading={busy === 'download'}
          variant="light" leftSection={<IconDownload size={16} />}>
          Download workbook
        </DisabledTooltipButton>
      </Group>

      {preview && (
        <PreviewPanel preview={preview} stale={stale} corrections={corrections} keyOf={keyOf}
          onCorrection={(key, c) => setCorrections((prev) => ({ ...prev, [key]: c }))}
          answers={answers} questionKeyOf={questionKeyOf}
          onAnswer={(key, a) => setAnswers((prev) => ({ ...prev, [key]: a }))}
          openingCorr={openingCorr} openingKeyOf={openingKeyOf}
          onOpening={(key, c) => setOpeningCorr((prev) => ({ ...prev, [key]: c }))} />
      )}
    </Stack>
  );
}

function PreviewPanel({
  preview: pv, stale, corrections, keyOf, onCorrection, answers, questionKeyOf, onAnswer, openingCorr, openingKeyOf,
  onOpening,
}: {
  preview: CgPreview; stale: boolean; corrections: Record<string, Correction>;
  keyOf: (lot: CgTransferInLot) => string;
  onCorrection: (key: string, c: Correction) => void;
  answers: Record<string, string>;
  questionKeyOf: (q: CgQuestion) => string;
  onAnswer: (key: string, answer: string) => void;
  openingCorr: Record<string, Correction>;
  openingKeyOf: (o: CgOpeningUnits) => string;
  onOpening: (key: string, c: Correction) => void;
}) {
  const problems = [
    ...pv.input_errors.map((e) => ({ where: '', reason: e })),
    ...pv.blocking.map((b) => ({
      where: `${b.portfolio}${b.row && !b.question_key ? ` · row ${b.row}` : ''}`,
      reason: b.question_key ? `${b.reason} (see “Questions for you” below)` : b.reason,
    })),
    ...pv.checks.filter((c) => !c.ok).map((c) => ({ where: 'Consistency check failed', reason: `${c.name} — ${c.detail}` })),
  ];
  const ready = pv.publishable && problems.length === 0;
  const [showAllReview, setShowAllReview] = useState(false);
  const review = showAllReview ? pv.review_items : pv.review_items.slice(0, 25);

  return (
    <Card withBorder>
      <Stack gap="md">
        <Group justify="space-between">
          <Text fw={600}>3 · Preview — {pv.client_label || '(no client label)'}</Text>
          <Text size="xs" c="dimmed">
            Calculation date {pv.as_of} · prices {pv.price_date}{pv.price_date_defaulted ? ' (calculation date)' : ''} ·
            run ID {pv.run_id.slice(0, 8)}…
          </Text>
        </Group>

        {stale && (
          <Alert color="orange" icon={<IconAlertTriangle size={16} />} title="Out of date">
            The inputs changed since this preview. Preview again to see figures for what is on screen now.
          </Alert>
        )}

        {!ready ? (
          <Alert color="red" icon={<IconX size={16} />} title={`The run stops: ${problems.length} problem${problems.length === 1 ? '' : 's'} to fix`}>
            <Text size="sm" mb={6}>
              The tool never guesses: nothing is computed while any of these remain (spec §5).
            </Text>
            <List size="sm" spacing={2}>
              {problems.slice(0, 30).map((p, i) => (
                <List.Item key={i}>{p.where && <b>{p.where}: </b>}{p.reason}</List.Item>
              ))}
            </List>
            {problems.length > 30 && <Text size="sm" mt={4}>…and {problems.length - 30} more.</Text>}
          </Alert>
        ) : (
          <Alert color="green" icon={<IconCheck size={16} />} title="All checks passed — ready to download">
            <List size="sm" spacing={2}>
              {pv.checks.map((c) => <List.Item key={c.name}>{c.name} <Text span c="dimmed" size="xs">({c.detail})</Text></List.Item>)}
            </List>
          </Alert>
        )}

        {pv.questions.length > 0 && (
          <QuestionsPanel questions={pv.questions} answers={answers} keyOf={questionKeyOf} onAnswer={onAnswer} />
        )}

        {pv.history === 'partial' && (
          <WindowPanel windows={pv.windows} opening={pv.opening_units} corrections={openingCorr} keyOf={openingKeyOf}
            onCorrection={onOpening} />
        )}

        <Table withTableBorder verticalSpacing={4} fz="sm">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Portfolio</Table.Th><Table.Th>File</Table.Th><Table.Th ta="right">Rows</Table.Th>
              <Table.Th ta="right">Open positions</Table.Th><Table.Th ta="right">Open lots</Table.Th>
              <Table.Th ta="right">Sales</Table.Th><Table.Th ta="right">Closed</Table.Th>
              <Table.Th ta="right">Code changes</Table.Th><Table.Th ta="right">Cash (EUR)</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {pv.portfolios.map((p) => (
              <Table.Tr key={p.label}>
                <Table.Td>{p.label}</Table.Td><Table.Td c="dimmed">{p.file_name}</Table.Td>
                <Table.Td ta="right">{p.rows}</Table.Td><Table.Td ta="right">{p.positions}</Table.Td>
                <Table.Td ta="right">{p.open_lots}</Table.Td><Table.Td ta="right">{p.sales}</Table.Td>
                <Table.Td ta="right">{p.closed_instruments}</Table.Td><Table.Td ta="right">{p.code_changes}</Table.Td>
                <Table.Td ta="right">{eur(p.cash_value_eur)}</Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>

        {pv.years.length > 0 && (
          <Stack gap={4}>
            <Text fw={600} size="sm">Realised gains and tax per calendar year (EUR, all portfolios)</Text>
            <Table withTableBorder verticalSpacing={4} fz="sm">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Year</Table.Th>
                  <Table.Th ta="right">Net gain ≤365 d</Table.Th><Table.Th ta="right">Tax ≤365 d ({pct(pv.short_rate)})</Table.Th>
                  <Table.Th ta="right">Net gain &gt;365 d</Table.Th><Table.Th ta="right">Tax &gt;365 d ({pct(pv.long_rate)})</Table.Th>
                  <Table.Th ta="right">Total tax</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {pv.years.map((y) => (
                  <Table.Tr key={y.year}>
                    <Table.Td>{y.year}</Table.Td>
                    <Table.Td ta="right">{eur(y.short_gain_eur)}{y.short_tax == null && <Partial />}</Table.Td>
                    <Table.Td ta="right">{tax(y.short_tax)}</Table.Td>
                    <Table.Td ta="right">{eur(y.long_gain_eur)}{y.long_tax == null && <Partial />}</Table.Td>
                    <Table.Td ta="right">{tax(y.long_tax)}</Table.Td>
                    <Table.Td ta="right" fw={600}>{tax(y.total_tax)}</Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
            {pv.years.some((y) => y.total_tax == null) && (
              <Text size="xs" c="dimmed">
                “Not computable”: the group contains a sale that used units held before day 1, whose cost is unknown.
                The net gain shown (marked partial) counts the certified sales only.
              </Text>
            )}
          </Stack>
        )}
        {pv.if_sold_today && (
          pv.if_sold_today.extra_tax == null ? (
            <Text size="sm">
              <b>If every open lot were sold on {pv.as_of}</b>: not computable — it depends on units bought before day 1,
              whose cost is unknown (sold this year, or still held). Entering their date and cost under “Units held
              before day 1” makes it computable.
            </Text>
          ) : (
            <Text size="sm">
              <b>If every open lot were sold on {pv.as_of}</b> (custodian prices, before sale costs): {pv.if_sold_today.year} tax
              would go from {eur(pv.if_sold_today.realised_tax)} to {eur(pv.if_sold_today.combined_tax)} —
              <b> {eur(pv.if_sold_today.extra_tax)} extra</b>. Per-lot detail, days to qualify and the method are in the workbook.
            </Text>
          )
        )}

        {pv.snapshots.length > 0 && <SnapshotsPanel snapshots={pv.snapshots} />}

        {pv.transfer_in_lots.length > 0 && (
          <TransferInCorrections lots={pv.transfer_in_lots} corrections={corrections} keyOf={keyOf}
            onCorrection={onCorrection} />
        )}

        {pv.review_items.length > 0 && (
          <Alert color="yellow" icon={<IconAlertTriangle size={16} />}
            title={`${pv.review_items.length} item${pv.review_items.length === 1 ? '' : 's'} to review (not blocking)`}>
            <Text size="sm" mb={6}>Also listed on the workbook&apos;s Issues sheet.</Text>
            <List size="sm" spacing={2}>
              {review.map((r, i) => (
                <List.Item key={i}>
                  <Badge size="xs" variant="light" mr={6}>{r.type}</Badge>
                  {[r.portfolio, r.instrument, r.date].filter(Boolean).join(' · ')}{(r.portfolio || r.instrument) ? ': ' : ''}{r.detail}
                </List.Item>
              ))}
            </List>
            {pv.review_items.length > 25 && (
              <Text size="sm" mt={4} c="blue" style={{ cursor: 'pointer' }} onClick={() => setShowAllReview((v) => !v)}>
                {showAllReview ? 'Show fewer' : `Show all ${pv.review_items.length}`}
              </Text>
            )}
          </Alert>
        )}
      </Stack>
    </Card>
  );
}

function Partial() {
  return (
    <Tooltip label="Certified sales only: the group also has sales of units held before day 1, of unknown cost" withArrow>
      <Badge size="xs" color="orange" variant="light" ml={6}>partial</Badge>
    </Tooltip>
  );
}

/** Partial history: the certified window per portfolio, and the units held before day 1 with
 *  their status and the user's optional true date and EUR cost. */
function WindowPanel({ windows, opening, corrections, keyOf, onCorrection }: {
  windows: CgWindow[]; opening: CgOpeningUnits[]; corrections: Record<string, Correction>;
  keyOf: (o: CgOpeningUnits) => string;
  onCorrection: (key: string, c: Correction) => void;
}) {
  const day1Of = Object.fromEntries(windows.map((w) => [w.portfolio, w.day1]));
  return (
    <Stack gap={6}>
      <Text fw={600} size="sm">Partial history — the certified window</Text>
      <Table withTableBorder verticalSpacing={4} fz="sm">
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Portfolio</Table.Th><Table.Th>Day 1 (oldest operation)</Table.Th>
            <Table.Th>Every lot known from</Table.Th><Table.Th ta="right">Sales of units from before day 1</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {windows.map((w) => (
            <Table.Tr key={w.portfolio}>
              <Table.Td>{w.portfolio}</Table.Td><Table.Td>{w.day1 ?? '—'}</Table.Td>
              <Table.Td fw={600}>{w.certified_from ?? '—'}</Table.Td>
              <Table.Td ta="right">{w.uncertified_sales}</Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
      {opening.length === 0 ? (
        <Text size="sm" c="dimmed">No security held anything before day 1: every lot is known from day 1.</Text>
      ) : (
        <>
          <Text fw={600} size="sm" mt={4}>Units held before day 1 ({opening.length})</Text>
          <Text size="sm" c="dimmed">
            Worked out back from the custodian&apos;s quantities. Their purchase dates and costs are not in the export, so
            FIFO sells them first with no cost or gain. Units still held today are never certified from the export alone:
            if you know their true acquisition date (before day 1) and cost in EUR, enter both and <b>preview again</b>.
            Treated as one lot of that date and cost.
          </Text>
          <Table withTableBorder verticalSpacing={4} fz="sm">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Portfolio</Table.Th><Table.Th>Instrument</Table.Th>
                <Table.Th ta="right">Units before day 1</Table.Th><Table.Th>Status</Table.Th>
                <Table.Th>True acquisition date</Table.Th><Table.Th>True cost (EUR)</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {opening.map((o) => {
                const key = keyOf(o);
                const c = corrections[key] ?? { acquiredOn: '', costEur: '' };
                const held = Number(o.still_held) > 0;
                const day1 = day1Of[o.portfolio];
                return (
                  <Table.Tr key={key}>
                    <Table.Td>{o.portfolio}</Table.Td>
                    <Table.Td>{o.name || o.instrument}<Text size="xs" c="dimmed">{o.isin ?? o.instrument}</Text></Table.Td>
                    <Table.Td ta="right">{qty(o.quantity)}</Table.Td>
                    <Table.Td>
                      {o.corrected
                        ? <Badge color="green" variant="light">Certified by your date and cost</Badge>
                        : held
                          ? <Badge color="orange" variant="light">{qty(o.still_held)} still held · cost unknown</Badge>
                          : <Text size="sm">All sold by {o.sold_out_on}</Text>}
                    </Table.Td>
                    <Table.Td>
                      <TextInput size="xs" type="date" w={160} value={c.acquiredOn}
                        max={day1 ? dayBefore(day1) : undefined}
                        onChange={(e) => onCorrection(key, { ...c, acquiredOn: e.currentTarget.value })} />
                    </Table.Td>
                    <Table.Td>
                      <NumberInput size="xs" w={140} min={0} decimalScale={2} placeholder="required"
                        error={c.acquiredOn && c.costEur === '' ? 'Required' : undefined}
                        value={c.costEur} onChange={(v) => onCorrection(key, { ...c, costEur: typeof v === 'number' ? v : '' })} />
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        </>
      )}
    </Stack>
  );
}

/** The positions and FIFO lots at the end of the certified date and of the chosen date. */
function SnapshotsPanel({ snapshots }: { snapshots: CgSnapshot[] }) {
  return (
    <Stack gap={6}>
      {snapshots.map((s) => (
        <Stack key={`${s.portfolio}|${s.date}`} gap={4}>
          <Text fw={600} size="sm">
            {s.kind === 'certified' ? 'Positions and lots at the certified date' : 'Positions and lots on the chosen date'}
            {' — '}{s.portfolio}, end of {s.date}
          </Text>
          {s.positions.length === 0 ? (
            <Text size="sm" c="dimmed">No positions held.</Text>
          ) : (
            <Table withTableBorder verticalSpacing={2} fz="sm">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Instrument</Table.Th><Table.Th ta="right">Position</Table.Th>
                  <Table.Th>Lot acquired</Table.Th><Table.Th>How</Table.Th><Table.Th ta="right">Lot quantity</Table.Th>
                  <Table.Th ta="right">Lot cost (EUR)</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {s.positions.flatMap((p) => p.lots.map((l, i) => (
                  <Table.Tr key={`${p.instrument}|${i}`}>
                    <Table.Td>
                      {i === 0 && <>{p.name || p.instrument}<Text size="xs" c="dimmed">{p.isin ?? p.instrument} · {p.currency}</Text></>}
                    </Table.Td>
                    <Table.Td ta="right">{i === 0 ? qty(p.quantity) : ''}</Table.Td>
                    <Table.Td>{l.certain ? l.acquired_on : <Text span c="orange" size="sm">before day 1 (unknown)</Text>}</Table.Td>
                    <Table.Td>{l.source === 'OPENING' ? 'Held before day 1' : l.source === 'TRANSFER_IN' ? 'Transfer in' : 'Buy'}</Table.Td>
                    <Table.Td ta="right">{qty(l.quantity)}</Table.Td>
                    <Table.Td ta="right">{l.certain ? eur(l.cost_eur) : <Text span c="orange" size="sm">unknown</Text>}</Table.Td>
                  </Table.Tr>
                )))}
              </Table.Tbody>
            </Table>
          )}
        </Stack>
      ))}
      <Text size="xs" c="dimmed">The same tables, with sources and operation codes, are in the workbook.</Text>
    </Stack>
  );
}

function QuestionsPanel({ questions, answers, keyOf, onAnswer }: {
  questions: CgQuestion[]; answers: Record<string, string>;
  keyOf: (q: CgQuestion) => string;
  onAnswer: (key: string, answer: string) => void;
}) {
  return (
    <Stack gap={6}>
      <Text fw={600} size="sm">Questions for you ({questions.length})</Text>
      <Text size="sm" c="dimmed">
        The export is ambiguous here, so the tool asks instead of guessing. Choose an answer, then <b>preview again</b>.
        An answer is accepted only where the data confirms it afterwards (the rebuilt holding must still agree with the
        custodian); otherwise the run still stops. Each answer is listed on the workbook&apos;s Issues sheet.
      </Text>
      {questions.map((q) => {
        const key = keyOf(q);
        return (
          <Card key={q.key} withBorder padding="sm">
            <Stack gap={6}>
              <Group justify="space-between" align="flex-start" wrap="nowrap">
                <Text size="sm">{q.prompt}</Text>
                {q.chosen
                  ? <Badge color="green" variant="light">Answered</Badge>
                  : <Badge color="orange" variant="light">Waiting for your answer</Badge>}
              </Group>
              <Text size="xs" c="dimmed">
                {q.portfolio} · export row{q.rows.length === 1 ? '' : 's'} {q.rows.slice(0, 12).join(', ')}
                {q.rows.length > 12 ? '…' : ''}
              </Text>
              <Radio.Group value={answers[key] ?? ''} onChange={(v) => onAnswer(key, v)}>
                <Stack gap={4}>
                  <Radio value="" label="Not decided yet — the run stops" />
                  {q.answers.map((a) => (
                    <Radio key={a.value} value={a.value} label={a.label} disabled={!a.available}
                      description={a.available ? undefined : a.why_not} />
                  ))}
                </Stack>
              </Radio.Group>
            </Stack>
          </Card>
        );
      })}
    </Stack>
  );
}

function TransferInCorrections({ lots, corrections, keyOf, onCorrection }: {
  lots: CgTransferInLot[]; corrections: Record<string, Correction>;
  keyOf: (lot: CgTransferInLot) => string;
  onCorrection: (key: string, c: Correction) => void;
}) {
  return (
    <Stack gap={4}>
      <Group gap={6}>
        <ThemeIcon size="sm" variant="light" color="orange"><IconAlertTriangle size={14} /></ThemeIcon>
        <Text fw={600} size="sm">Transfers in ({lots.length})</Text>
      </Group>
      <Text size="sm" c="dimmed">
        The export does not carry the original purchase of a transferred-in holding, so its lot is dated at the transfer
        and costed at the EUR value the export gives the transfer. That can put a gain in the ≤365-day group wrongly. If
        you know the true acquisition date (and, optionally, the true cost in EUR), enter it and <b>preview again</b>.
        A transfer with no EUR value in the export needs both.
      </Text>
      <Table withTableBorder verticalSpacing={4} fz="sm">
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Portfolio</Table.Th><Table.Th>Instrument</Table.Th><Table.Th>Operation</Table.Th>
            <Table.Th>Transferred</Table.Th><Table.Th ta="right">Quantity</Table.Th><Table.Th ta="right">Value in the export (EUR)</Table.Th>
            <Table.Th>True acquisition date</Table.Th><Table.Th>True cost (EUR)</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {lots.map((l) => {
            const key = keyOf(l);
            const c = corrections[key] ?? { acquiredOn: '', costEur: '' };
            const noValue = l.cost_eur == null;
            return (
              <Table.Tr key={key}>
                <Table.Td>{l.portfolio}</Table.Td>
                <Table.Td>{l.name || l.instrument}<Text size="xs" c="dimmed">{l.isin ?? l.instrument}</Text></Table.Td>
                <Table.Td>{l.op_code}</Table.Td><Table.Td>{l.transfer_date}</Table.Td>
                <Table.Td ta="right">{qty(l.quantity)}</Table.Td>
                <Table.Td ta="right">{noValue ? <Text span c="red" size="sm">none</Text> : eur(l.cost_eur)}</Table.Td>
                <Table.Td>
                  <TextInput size="xs" type="date" w={160} value={c.acquiredOn} max={l.transfer_date}
                    onChange={(e) => onCorrection(key, { ...c, acquiredOn: e.currentTarget.value })} />
                </Table.Td>
                <Table.Td>
                  <NumberInput size="xs" w={140} min={0} decimalScale={2} disabled={!c.acquiredOn}
                    placeholder={noValue ? 'required' : 'keep'} error={noValue && c.acquiredOn && c.costEur === '' ? 'Required' : undefined}
                    value={c.costEur} onChange={(v) => onCorrection(key, { ...c, costEur: typeof v === 'number' ? v : '' })} />
                </Table.Td>
              </Table.Tr>
            );
          })}
        </Table.Tbody>
      </Table>
    </Stack>
  );
}
